import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Avatar,
  LinearProgress,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  Divider
} from '@mui/material';
import {
  LocationOn as LocationIcon,
  People as PeopleIcon,
  School as SchoolIcon,
  TrendingUp as TrendingUpIcon,
  Assessment as AssessmentIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  EmojiEvents as TrophyIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';

interface OfficeStats {
  _id: string;
  name: string;
  totalEmployees: number;
  activeEmployees: number;
  totalCourses: number;
  completedCourses: number;
  averageProgress: number;
  averageScore: number;
  employees: Array<{
    _id: string;
    firstName: string;
    lastName: string;
    email: string;
    position: string;
    isActive: boolean;
    progress: Array<{
      _id: string;
      course: {
        _id: string;
        title: string;
      };
      status: string;
      completionPercentage: number;
      finalScore: number;
      updatedAt: string;
    }>;
  }>;
}

interface OfficeProgressData {
  offices: OfficeStats[];
  summary: {
    totalOffices: number;
    totalEmployees: number;
    totalCourses: number;
    overallProgress: number;
    overallScore: number;
  };
}

const OfficeProgress: React.FC = () => {
  const { user } = useAuth();
  const [data, setData] = useState<OfficeProgressData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedOffice, setSelectedOffice] = useState<number>(0);

  useEffect(() => {
    fetchOfficeProgress();
  }, []);

  const fetchOfficeProgress = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.getOfficeProgress();
      setData(response);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error cargando datos de avance');
    } finally {
      setLoading(false);
    }
  };

  const getProgressColor = (progress: number) => {
    if (progress >= 80) return 'success';
    if (progress >= 60) return 'warning';
    return 'primary';
  };

  const getScoreColor = (score: number) => {
    if (score >= 80) return 'success';
    if (score >= 60) return 'warning';
    return 'primary';
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircleIcon color="success" fontSize="small" />;
      case 'in_progress':
        return <ScheduleIcon color="warning" fontSize="small" />;
      default:
        return <SchoolIcon color="action" fontSize="small" />;
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'completed':
        return 'Completado';
      case 'in_progress':
        return 'En Progreso';
      case 'not_started':
        return 'No Iniciado';
      default:
        return status;
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const filteredOffices = selectedOffice === 0 
    ? data?.offices || []
    : data?.offices.filter((_, index) => index === selectedOffice - 1) || [];

  if (loading) {
    return (
      <Container maxWidth="xl">
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
          <Typography variant="h6" sx={{ ml: 2 }}>
            Cargando análisis de sedes...
          </Typography>
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="xl">
        <Alert severity="error" sx={{ mt: 3 }}>
          {error}
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Box display="flex" alignItems="center" gap={2} mb={2}>
          <TrendingUpIcon sx={{ fontSize: 40, color: 'primary.main' }} />
          <Typography variant="h4" component="h1">
            Avance por Sede
          </Typography>
        </Box>
        <Typography variant="body1" color="text.secondary">
          Análisis ejecutivo del progreso de formación por sede
        </Typography>
      </Box>

      {/* Resumen General */}
      {data?.summary && (
        <Box sx={{ 
          display: 'grid', 
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
          gap: 3,
          mb: 4 
        }}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" gap={2}>
                <LocationIcon color="primary" />
                <Box>
                  <Typography variant="h4" fontWeight="bold">
                    {data.summary.totalOffices}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Sedes
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" gap={2}>
                <PeopleIcon color="primary" />
                <Box>
                  <Typography variant="h4" fontWeight="bold">
                    {data.summary.totalEmployees}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Empleados
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" gap={2}>
                <SchoolIcon color="primary" />
                <Box>
                  <Typography variant="h4" fontWeight="bold">
                    {data.summary.totalCourses}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Cursos Totales
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" gap={2}>
                <TrophyIcon color="primary" />
                <Box>
                  <Typography variant="h4" fontWeight="bold">
                    {Math.round(data.summary.overallScore)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Puntuación Promedio
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Box>
      )}

      {/* Pestañas de Sedes */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Tabs
            value={selectedOffice}
            onChange={(_, newValue) => setSelectedOffice(newValue)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{ borderBottom: 1, borderColor: 'divider' }}
          >
            <Tab label="Todas las Sedes" />
            {data?.offices.map((office) => (
              <Tab key={office._id} label={office.name} />
            ))}
          </Tabs>
        </CardContent>
      </Card>

      {/* Análisis por Sede */}
      {filteredOffices.map((office) => (
        <Card key={office._id} sx={{ mb: 4 }}>
          <CardContent>
            {/* Header de la Sede */}
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
              <Box>
                <Typography variant="h5" fontWeight="bold" gutterBottom>
                  {office.name}
                </Typography>
                <Box display="flex" gap={4}>
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Cursos Completados
                    </Typography>
                    <Typography variant="h6">
                      {office.completedCourses} / {office.totalCourses}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Progreso Promedio
                    </Typography>
                    <Typography variant="h6">
                      {Math.round(office.averageProgress)}%
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Puntuación Promedio
                    </Typography>
                    <Typography variant="h6">
                      {Math.round(office.averageScore)}
                    </Typography>
                  </Box>
                </Box>
              </Box>
              <Box textAlign="right">
                <Chip
                  label={`${Math.round(office.averageProgress)}% Progreso`}
                  color={getProgressColor(office.averageProgress) as any}
                  size="medium"
                />
              </Box>
            </Box>

            {/* Barra de Progreso de la Sede */}
            <Box mb={3}>
              <Box display="flex" justifyContent="space-between" mb={1}>
                <Typography variant="body2" color="text.secondary">
                  Progreso General de la Sede
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {Math.round(office.averageProgress)}%
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={office.averageProgress}
                color={getProgressColor(office.averageProgress) as any}
                sx={{ height: 8, borderRadius: 4 }}
              />
            </Box>

            {/* Solo mostrar detalles de empleados cuando se selecciona una sede específica */}
            {selectedOffice > 0 && (
              <>
                <Divider sx={{ my: 3 }} />

                {/* Tabla de Empleados de la Sede */}
                <Typography variant="h6" gutterBottom>
                  Detalle por Empleado
                </Typography>
                {office.employees.length === 0 ? (
                  <Alert severity="info" sx={{ mt: 2 }}>
                    Esta sede no tiene empleados con cursos asignados actualmente. 
                    Esto es normal si los empleados de esta sede no requieren formación específica.
                  </Alert>
                ) : (
                  <TableContainer component={Paper} variant="outlined">
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Empleado</TableCell>
                        <TableCell>Cargo</TableCell>
                        <TableCell>Estado</TableCell>
                        <TableCell>Cursos Asignados</TableCell>
                        <TableCell>Progreso Promedio</TableCell>
                        <TableCell>Puntuación Promedio</TableCell>
                        <TableCell>Última Actividad</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {office.employees.map((employee) => {
                        const avgProgress = employee.progress.length > 0 
                          ? employee.progress.reduce((sum, p) => sum + p.completionPercentage, 0) / employee.progress.length
                          : 0;
                        const avgScore = employee.progress.length > 0
                          ? employee.progress.reduce((sum, p) => sum + (p.finalScore || 0), 0) / employee.progress.length
                          : 0;
                        const lastActivity = employee.progress.length > 0
                          ? Math.max(...employee.progress.map(p => new Date(p.updatedAt).getTime()))
                          : 0;

                        return (
                          <TableRow key={employee._id} hover>
                            <TableCell>
                              <Box display="flex" alignItems="center" gap={2}>
                                <Avatar sx={{ bgcolor: 'primary.main' }}>
                                  {employee.firstName.charAt(0)}
                                </Avatar>
                                <Box>
                                  <Typography variant="subtitle2" fontWeight="bold">
                                    {employee.firstName} {employee.lastName}
                                  </Typography>
                                  <Typography variant="body2" color="text.secondary">
                                    {employee.email}
                                  </Typography>
                                </Box>
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">
                                {employee.position || 'N/A'}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={employee.isActive ? 'Activo' : 'Inactivo'}
                                color={employee.isActive ? 'success' : 'error'}
                                size="small"
                              />
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">
                                {employee.progress.length} cursos
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Box display="flex" alignItems="center" gap={1}>
                                <Typography variant="body2">
                                  {Math.round(avgProgress)}%
                                </Typography>
                                <LinearProgress
                                  variant="determinate"
                                  value={avgProgress}
                                  color={getProgressColor(avgProgress) as any}
                                  sx={{ width: 60, height: 6 }}
                                />
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={Math.round(avgScore)}
                                color={getScoreColor(avgScore) as any}
                                size="small"
                              />
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">
                                {lastActivity ? formatDate(new Date(lastActivity).toISOString()) : 'N/A'}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
                )}

                {/* Detalle de Cursos por Empleado */}
                {office.employees.length > 0 && (
                  <Box mt={3}>
                    <Typography variant="h6" gutterBottom>
                      Progreso Detallado por Curso
                    </Typography>
                    {office.employees.map((employee) => (
                    <Card key={employee._id} variant="outlined" sx={{ mb: 2 }}>
                      <CardContent>
                        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                          {employee.firstName} {employee.lastName}
                        </Typography>
                        <Box sx={{ 
                          display: 'grid', 
                          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
                          gap: 2 
                        }}>
                          {employee.progress.map((progress) => (
                            <Card variant="outlined" key={progress._id}>
                              <CardContent sx={{ p: 2 }}>
                                <Box display="flex" alignItems="center" gap={1} mb={1}>
                                  {getStatusIcon(progress.status)}
                                  <Typography variant="subtitle2" fontWeight="bold">
                                    {progress.course.title}
                                  </Typography>
                                </Box>
                                <Box mb={1}>
                                  <Box display="flex" justifyContent="space-between" mb={0.5}>
                                    <Typography variant="caption" color="text.secondary">
                                      Progreso
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                      {Math.round(progress.completionPercentage)}%
                                    </Typography>
                                  </Box>
                                  <LinearProgress
                                    variant="determinate"
                                    value={progress.completionPercentage}
                                    color={getProgressColor(progress.completionPercentage) as any}
                                    sx={{ height: 6 }}
                                  />
                                </Box>
                                <Box display="flex" justifyContent="space-between" alignItems="center">
                                  <Chip
                                    label={getStatusLabel(progress.status)}
                                    color={progress.status === 'completed' ? 'success' : 'default'}
                                    size="small"
                                  />
                                  {progress.finalScore && (
                                    <Chip
                                      label={`${Math.round(progress.finalScore)}%`}
                                      color={getScoreColor(progress.finalScore) as any}
                                      size="small"
                                    />
                                  )}
                                </Box>
                                <Typography variant="caption" color="text.secondary" display="block" mt={1}>
                                  Actualizado: {formatDate(progress.updatedAt)}
                                </Typography>
                              </CardContent>
                            </Card>
                          ))}
                        </Box>
                      </CardContent>
                    </Card>
                    ))}
                  </Box>
                )}
              </>
            )}
          </CardContent>
        </Card>
      ))}
    </Container>
  );
};

export default OfficeProgress;

